<template>
  <v-calendar
    v-model:now="today"
    type="month"
    :events="events"
    color="primary"
    first-day-of-week="1"
    :hide-week-number="true"  
    today-label="Dnes"
    :show-week="false"
    :show-adjacent-months="true"
    :text="loading ? 'Načítám...' : 'Dnes'"
  >
  <template v-slot:prepend>
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="primary"
      size="36"
    ></v-progress-circular>
  </template>
  <template v-slot:event="{ event }">
  <div class="race-calendar-event" :class="{ canceled: event.canceled }" :style="{ backgroundColor: event.backgroundColor }">
    <div class="name-lines">
      <router-link :to="`/soutez/${event.eventId}`" class="event-name">
        {{ event.eventName }}
      </router-link>
      <div class="race-name">{{ event.raceName }}</div>
    </div>
    <div class="details">
      <v-chip small color="secondary" class="entry-chip" density="compact">
        <v-icon left small>mdi-account</v-icon>
        {{ event.entries }}
      </v-chip>
      <v-chip small class="text-grey-darken-2" density="compact">
        {{ event.clubShortcut }}
      </v-chip>
      <template v-if="event.raceName">
        <v-chip small class="race-level-chip text-deep-orange-darken-2" density="compact">
          {{ raceLevelAbbr[event.raceLevel] || event.raceLevel }}
        </v-chip>
      </template>
      <template v-else>
        <!-- Pokud není definována race, zobrazí se inline ikony místo chipu -->
        <router-link
          v-if="event.entryIsAllow"
          :to="`/soutez/${event.eventId}/prihlaska/`"
          class="entry-link"
        >
          <v-icon color="green" size="medium" title="přihlásit se" class="mr-1">mdi-account-plus</v-icon>
        </router-link>
        <a v-if="event.bulletin" :href="formatFileLink(event.bulletin)" target="_blank">
          <v-icon color="primary" size="small" title="propozice">mdi-text-box</v-icon>
        </a>
        <a v-if="event.info" :href="formatFileLink(event.info)" target="_blank">
          <v-icon color="primary" size="small" title="pokyny">mdi-information-slab-box</v-icon>
        </a>
        <a v-if="event.startlist" :href="formatFileLink(event.startlist)" target="_blank">
          <v-icon color="primary" size="small" title="startovka">mdi-timer-play</v-icon>
        </a>
        <a v-if="event.results" :href="formatFileLink(event.results)" target="_blank">
          <v-icon color="primary" size="small" title="výsledky">mdi-trophy</v-icon>
        </a>
      </template>
      <!-- Lock ikona, pokud je event omezen (např. visibility === 'CLUB') -->
      <v-icon v-if="event.visibility === 'CLUB'" size="small" class="ml-1">
        mdi-lock-open
      </v-icon>
    </div>
    <!-- Pokud je definován race, zobrazí se další řádek s ikonami -->
    <div class="icons" v-if="event.raceName">
      <router-link
        v-if="event.entryIsAllow"
        :to="`/soutez/${event.eventId}/prihlaska/`"
        class="entry-link mr-4"
      >
        <v-icon color="green" size="medium" title="přihlásit se">mdi-account-plus</v-icon>
      </router-link>
      <a v-if="event.bulletin" :href="formatFileLink(event.bulletin)" target="_blank">
        <v-icon color="primary" size="small" title="propozice">mdi-text-box</v-icon>
      </a>
      <a v-if="event.info" :href="formatFileLink(event.info)" target="_blank">
        <v-icon color="primary" size="small" title="pokyny">mdi-information-slab-box</v-icon>
      </a>
      <a v-if="event.startlist" :href="formatFileLink(event.startlist)" target="_blank">
        <v-icon color="primary" size="small" title="startovka">mdi-timer-play</v-icon>
      </a>
      <a v-if="event.results" :href="formatFileLink(event.results)" target="_blank">
        <v-icon color="primary" size="small" title="výsledky">mdi-trophy</v-icon>
      </a>
    </div>
  </div>
</template>

  </v-calendar>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import apiClient from '/api.js';

// Aktuální datum pro kalendář
const today = ref(new Date())


/* Přijímáme pouze currentCalendar jako prop */
const props = defineProps({
  currentCalendar: {
    type: String,
    required: true
  }
})


// Pole barev, které přiřadíme eventům (všechny races z jednoho eventu obdrží stejnou barvu)
const eventColors = ['#ECEFF1', '#ffebee', '#e8f5e9', '#ede7f6', '#fff8e1', '#e0f7fa', '#f1f8e9']


// Lokální mapping pro zkratky typu závodu
const raceLevelAbbr = {
  INTERNATIONAL: 'svět',
  NATIONAL: 'I.',
  REGIONAL: 'II.',
  DISTRICT: 'III.',
  TRAINING: 'trén',
  SELECTIONS: 'nom',
  OTHER: 'ost'
}


const events = ref([])
const loading = ref(true)

// Funkce pro načtení eventů
const fetchEvents = async (year, period) => {
  loading.value = true
  try {
    const response = await apiClient.get(`/event/?year=${year}&period=${period}`);
    events.value = response.data.flatMap(ev => {
      const backgroundColor = eventColors[ev.id % eventColors.length];

    
    // 1) Zpracování skutečných races, pokud nějaké event.races existují.
    const raceEntries = Array.isArray(ev.races)
      ? ev.races
          .map(race => {
            const raceDate = new Date(race.race_date);
            if (isNaN(raceDate.getTime())) return null;
            return {
              eventId: ev.id,
              eventName: ev.event_name,
              clubShortcut: ev.club_shortcut,
              visibility: ev.visibility,
              raceName: race.race_name,
              start: raceDate,
              // I když jde o jednodenní závod, nastavíme end jako kopii startu,
              // aby Vuetify měl obě hodnoty (jinak může dojít k chybám)
              end: new Date(raceDate.getTime()),
              entries: race.entries,
              raceLevel: race.race_level, // očekáváme hodnotu např. 'NATIONAL', 'DISTRICT', atd.
              bulletin: race.bulletin || ev.bulletin,
              info: race.info || ev.info,
              startlist: race.startlist || ev.startlist,
              results: race.results || ev.results,
              entryIsAllow: ev.entry_is_allow,
              canceled:ev.canceled,
              backgroundColor
            };
          })
          .filter(x => x !== null)
      : [];
    
    // Vytvoříme množinu dat, kdy jsou již nějaké race
    const raceDates = new Set(
      raceEntries.map(entry => entry.start.toISOString().split("T")[0])
    );
    
    // 2) Iterujeme přes všechny dny eventu (od event_date_start do event_date_end včetně)
    const dummyEntries = [];
    const eventStart = new Date(ev.event_date_start);
    const eventEnd = new Date(ev.event_date_end);
    
    // Předpokládáme, že event_date_end je inkluzivní
    for (let d = new Date(eventStart); d <= eventEnd; d.setDate(d.getDate() + 1)) {
      const ds = d.toISOString().split("T")[0]; // formát YYYY-MM-DD
      if (!raceDates.has(ds)) {
        // Pokud pro tento den není žádný race, vytvoříme dummy položku
        dummyEntries.push({
          eventId: ev.id,
          eventName: ev.event_name,
          clubShortcut: ev.club_shortcut,
          visibility: ev.visibility,
          raceName: "", // Nebudeme zobrazovat race name
          start: new Date(d),
          end: new Date(d), // nastavíme end stejný jako start (jednodenní položka)
          entries: ev.entries, // případně 0, protože pro tento den není registrace na konkrétní race
          raceLevel: "", // nebo lze vypsat prázdný string
          bulletin: ev.bulletin,
          info: ev.info,
          startlist: ev.startlist,
          results: ev.results,
          entryIsAllow: ev.entry_is_allow,
          canceled:ev.canceled,
          backgroundColor
        });
      }
    }
    // Vrátíme všechny skutečné races a dummy položky
    return [...raceEntries, ...dummyEntries];
  });


  } catch (error) {
    console.error('Error fetching events:', error);
  } finally {
    loading.value = false;
  }
};

// Načtení dat při mountu, dle aktuálně zobrazeného viewDate
onMounted(() => {
  if (props.currentCalendar === 'graphical') {
    
    fetchEvents(2025, "all") // ToDo: Zde by měl být rok a období z aktuálního výběru v kalendáři. vuetify 3.8.1 to zatím neumí.
  }
})


// Funkce pro sestavení URL odkazu na soubory – upravte dle své konfigurace
const formatFileLink = (link) => {
  const BaseUrl = ref(process.env.VUE_APP_API_BASE_URL.replace(/\/+$/, '').replace(/\/api\/?$/, '')); // Odstraní "api/" a nadbytečná lomítka
  return `${BaseUrl.value}${link}`
}
</script>

<style scoped>
.v-calendar {
  min-width: 1100px;
}

.race-calendar-event {
  padding: 4px;
  border-radius: 4px;
  margin: 2px;
  font-size: 0.85rem;
  color: #000;
}

/* Zajišťuje, že eventName a raceName jsou na samostatných řádcích se skrytím přetečení */
.name-lines {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.event-name {
  font-weight: bold;
  line-height: 1.6;
}
.race-name {
  font-style: italic;
  line-height: 1.2;
}

/* Detailní informace – v-chip pro počet přihlášených a chip pro typ závodu */
.details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
}
/* .entry-chip {
  font-size: 0.7rem;
  line-height: 1;
}
.race-level-chip {
  font-size: 0.7rem;
  line-height: 1;
} */

/* Ikony pro odkazy a přihlášení */
.icons {
  margin-top: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.icons a,
.icons .entry-link {
  margin-right: 4px;
}

.canceled {
  text-decoration: line-through;
}

:deep().v-calendar-weekly__day-alldayevents-container {
    min-height: 0px!important;
}

</style>
